/**
 * Credit to MUI team @ https://mui.com
 */
import { Box, type SxPropsWithTheme } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import * as React from 'react';

import type { CloseReason } from '../../types';
import { useEventCallback } from '../../utils';

export interface CustomSnackbarProps extends PropsWithChildren {
  open: boolean;
  id?: string | number;
  autoHideDuration: number | null | undefined;
  disableWindowBlurListener?: boolean;
  onClose?: (event: null, reason: CloseReason, key: string | number) => void;
  sx?: SxPropsWithTheme;
}

const CustomSnackbar = React.forwardRef<HTMLDivElement, CustomSnackbarProps>(
  (props: Readonly<CustomSnackbarProps>, ref) => {
    const {
      id = 'snackbar',
      open,
      children,
      autoHideDuration,
      disableWindowBlurListener = false,
      sx,
      onClose,
    } = props;

    const timerAutoHide = React.useRef<ReturnType<typeof setTimeout>>();

    const handleClose = useEventCallback((...args: [null, CloseReason, string | number]) => {
      if (onClose) {
        onClose(...args);
      }
    });

    const setAutoHideTimer = useEventCallback((autoHideDurationParam?: number | null) => {
      if (!onClose || autoHideDurationParam == null) {
        return;
      }

      if (timerAutoHide.current) {
        clearTimeout(timerAutoHide.current);
      }
      timerAutoHide.current = setTimeout(() => {
        handleClose(null, 'timeout', id);
      }, autoHideDurationParam);
    });

    React.useEffect(() => {
      if (open) {
        setAutoHideTimer(autoHideDuration);
      }

      return () => {
        if (timerAutoHide.current) {
          clearTimeout(timerAutoHide.current);
        }
      };
    }, [open, autoHideDuration, setAutoHideTimer]);

    /**
     * Pause the timer when the user is interacting with the Snackbar
     * or when the user hide the window.
     */
    const handlePause = () => {
      if (timerAutoHide.current) {
        clearTimeout(timerAutoHide.current);
      }
    };

    /**
     * Restart the timer when the user is no longer interacting with the Snackbar
     * or when the window is shown back.
     */
    const handleResume = React.useCallback(() => {
      if (autoHideDuration != null) {
        setAutoHideTimer(autoHideDuration * 0.5);
      }
    }, [autoHideDuration, setAutoHideTimer]);

    const handleMouseEnter: React.MouseEventHandler<HTMLDivElement> = () => {
      handlePause();
    };

    const handleMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
      handleResume();
    };

    React.useEffect(() => {
      if (!disableWindowBlurListener && open) {
        window.addEventListener('focus', handleResume);
        window.addEventListener('blur', handlePause);

        return () => {
          window.removeEventListener('focus', handleResume);
          window.removeEventListener('blur', handlePause);
        };
      }

      return undefined;
    }, [disableWindowBlurListener, handleResume, open]);

    return (
      <Box
        id={id?.toString()}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={sx}
      >
        {children}
      </Box>
    );
  },
);

CustomSnackbar.displayName = 'CustomSnackbar';

export default React.memo(CustomSnackbar);
